import { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

interface Profile {
  id: string;
  email: string;
  full_name: string | null;
  phone: string | null;
  date_of_birth: string | null;
  street_address: string | null;
  suburb: string | null;
  state: string | null;
  postcode: string | null;
  country: string | null;
  bio: string | null;
  is_verified: boolean;
  verification_status: string | null;
  verification_notes: string | null;
  created_at: string;
}

interface AuthContextType {
  user: User | null;
  profile: Profile | null;
  loading: boolean;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signInWithFacebook: () => Promise<{ error: any }>;
  refreshProfile: () => Promise<Profile | null>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const isInitialized = useRef(false);
  const profileFetchInProgress = useRef(false);
  const currentUserIdRef = useRef<string | null>(null);

  const fetchProfile = async (userId: string) => {
    // Prevent concurrent profile fetches
    if (profileFetchInProgress.current) {
      return null;
    }

    profileFetchInProgress.current = true;
    
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      // ✅ CRITICAL: Only apply results if this is still the current user
      if (currentUserIdRef.current !== userId) {
        return null;
      }

      // Check if profile doesn't exist (either error PGRST116 or data is null)
      if (error?.code === 'PGRST116' || (!data && !error)) {
        return null;
      }

      if (error) {
        throw error;
      }

      if (data) {
        setProfile(data);
        return data;
      }
    } catch (err) {
      return null;
    } finally {
      profileFetchInProgress.current = false;
    }
  };

  // Helper function to clear invalid session
  const clearInvalidSession = async () => {
    try {
      // Clear local storage first
      localStorage.removeItem('supabase.auth.token');
      
      // Sign out from Supabase
      await supabase.auth.signOut({ scope: 'local' });
    } catch (err) {
      // silently handle
    }
    setUser(null);
    setProfile(null);
    currentUserIdRef.current = null;
  };

  // Effect 1: One-time auth initialization and listener registration
  useEffect(() => {
    // Prevent multiple initializations
    if (isInitialized.current) {
      return;
    }

    isInitialized.current = true;

    // Initialize auth state
    const initAuth = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          // Check if it's a refresh token error
          if (error.message.includes('refresh') || 
              error.message.includes('Refresh Token') ||
              error.message.includes('Invalid Refresh Token')) {
            await clearInvalidSession();
          } else {
            await supabase.auth.signOut({ scope: 'local' });
            setUser(null);
            setProfile(null);
            currentUserIdRef.current = null;
          }
          setLoading(false);
          return;
        }
        
        if (session?.user) {
          setUser(session.user);
          // Profile will be fetched by Effect 2
        }
      } catch (error: any) {
        // Check if it's a refresh token error
        if (error.message?.includes('refresh') || 
            error.message?.includes('Refresh Token') ||
            error.message?.includes('Invalid Refresh Token')) {
          await clearInvalidSession();
        } else {
          await supabase.auth.signOut({ scope: 'local' });
          setUser(null);
          setProfile(null);
          currentUserIdRef.current = null;
        }
      } finally {
        setLoading(false);
      }
    };

    initAuth();

    // Listen for auth changes (registered ONCE)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (event === 'SIGNED_IN') {
          if (session?.user) {
            setUser(session.user);

            // Check if this is a new OAuth user by looking for an existing profile
            const isOAuthUser = session.user.app_metadata?.provider === 'google' || session.user.app_metadata?.provider === 'facebook';
            if (isOAuthUser) {
              try {
                const { data: existingProfile } = await supabase
                  .from('profiles')
                  .select('id')
                  .eq('id', session.user.id)
                  .single();

                if (!existingProfile) {
                  // No profile yet — this is a brand new OAuth signup
                  const fullName = session.user.user_metadata?.full_name || session.user.user_metadata?.name || '';
                  const email = session.user.email || '';
                  await fetch(`https://sibpoabenkejwltenabf.supabase.co/functions/v1/send-welcome-email`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InNpYnBvYWJlbmtlandsdGVuYWJmIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjgyMTM0NjgsImV4cCI6MjA4Mzc4OTQ2OH0.9MFPEGIggob9HTdKkVTk7qzDo5ccpUSAdQzop8TRAEY'
                    },
                    body: JSON.stringify({ email, full_name: fullName })
                  });
                }
              } catch (err) {
                // Silently fail — welcome email is non-critical
                console.warn('OAuth welcome email check failed:', err);
              }
            }
          }
        } else if (event === 'SIGNED_OUT') {
          setUser(null);
          setProfile(null);
          profileFetchInProgress.current = false;
          currentUserIdRef.current = null;
        } else if (event === 'USER_UPDATED') {
          if (session?.user) {
            setUser(session.user);
          }
        } else if (event === 'TOKEN_REFRESHED') {
          // ✅ Do nothing - token refresh doesn't change user identity
        }
        
        setLoading(false);
      }
    );

    return () => {
      subscription.unsubscribe();
    };
  }, []); // ✅ Empty dependency array - only run once

  // Effect 2: Profile management based on user ID
  useEffect(() => {
    // Reset fetch flag whenever user changes
    profileFetchInProgress.current = false;
    
    // ✅ Update current user ID reference BEFORE fetching
    currentUserIdRef.current = user?.id || null;
    
    if (user?.id) {
      fetchProfile(user.id);
    } else {
      setProfile(null);
    }
  }, [user?.id]); // ✅ Runs when user ID changes

  const signUp = async (email: string, password: string, fullName: string) => {
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName,
          },
          emailRedirectTo: `${window.location.origin}/email-verified`,
        },
      });

      if (error) {
        return { error };
      }

      return { error: null };
    } catch (err: any) {
      return { 
        error: { 
          message: err.message || 'An unexpected error occurred',
          code: 'unexpected_error'
        } 
      };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        return { error };
      }
      
      return { error: null };
    } catch (err: any) {
      return { error: err };
    }
  };

  const signInWithGoogle = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/`,
        },
      });

      if (error) return { error };
      return { error: null };
    } catch (err: any) {
      return { error: err };
    }
  };

  const signInWithFacebook = async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'facebook',
        options: {
          redirectTo: `${window.location.origin}/`,
        },
      });

      if (error) return { error };
      return { error: null };
    } catch (err: any) {
      return { error: err };
    }
  };

  const signOut = async () => {
    // Clear state FIRST
    setUser(null);
    setProfile(null);
    profileFetchInProgress.current = false;
    currentUserIdRef.current = null;
    
    // Clear local storage
    localStorage.removeItem('supabase.auth.token');
    
    // Sign out from Supabase
    await supabase.auth.signOut();
    
    // ✅ NO NAVIGATION HERE - let caller handle it
  };

  const refreshProfile = async () => {
    if (user) {
      const result = await fetchProfile(user.id);
      return result;
    } else {
      return null;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        loading,
        signUp,
        signIn,
        signOut,
        signInWithGoogle,
        signInWithFacebook,
        refreshProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
